/* transformTreatmentData.ts */

/* Axios */
import { HttpStatusCode } from "axios";

/* Utils */
import { ClientError } from "src/utils/status-handling/ClientError";
import {
  computeNodeTotals,
  markToGroupNodes,
  createGroupedLinks,
  aggregateDuplicateLinks,
  reindexSankeyDiagram,
  getBaseName,
  getSuffix,
  resolveGroupForCombinedName,
} from "src/utils/helper/treatment-patterns/helper";
import { transform } from "src/utils/helper/string-transform/StringTransformer";

/* Constants */
import { ChartType } from "src/shared/constants/charts/Chart";
import { Insights } from "src/shared/constants/common/Common";
import { ErrorMessage } from "src/shared/constants/common/ErrorMessage";
import { orderCohortGroups } from "src/shared/constants/charts/NoValueStates";

/* Config */
import { TREATMENT_GROUP_MAP } from "src/config/treatmentGroupMap";

/* Interfaces */
import {
  SankeyDiagramProps,
  SankeyBoxplotProps,
  NodeProps,
} from "src/shared/interfaces/charts/SankeyDiagram";

import { BaseChartProps } from "src/shared/interfaces/charts/BaseChart";
import { TreatmentPatternsProps } from "src/shared/interfaces/TreatmentPatterns";
import { GlobalMessageType } from "src/shared/constants/common/GlobalMessage";
import { BoxplotProps } from "src/shared/interfaces/Boxplot";

/**
 * Aggregate boxplot entries by treatment group.
 *
 * For each `BoxplotProps` entry inside every `SankeyBoxplotProps`, the
 * treatment name is resolved via `groupMap`:
 *  - If the base name (without the trailing `(TLn)` suffix) maps to a group,
 *    the entry is merged into a synthetic group entry whose name is
 *    `<groupName> (TLn)`.  The `y` arrays of all members are concatenated.
 *  - If the base name is not in `groupMap`, the entry passes through unchanged.
 *
 * Non-mapped entries and group entries are ordered so that group entries
 * appear first (preserving the order in which groups were first encountered),
 * followed by any remaining raw entries in their original order.
 *
 * @param {SankeyBoxplotProps[]} boxplots - Raw boxplot data from the API.
 * @param {Record<string, string>} groupMap - Treatment-name → group-name map.
 * @returns {SankeyBoxplotProps[]}
 */
export const groupBoxplotsByTreatmentMap = (
  boxplots: SankeyBoxplotProps[],
  groupMap: Record<string, string>
): SankeyBoxplotProps[] => {
  return boxplots.map((item) => {
    /* Maps "GroupName (TLn)" → accumulated BoxplotProps for that group. */
    const groupEntries = new Map<string, BoxplotProps>();
    /* Raw entries whose base name was not found in groupMap. */
    const ungroupedEntries: BoxplotProps[] = [];

    item.data.forEach((point) => {
      const suffix = getSuffix(point.name);
      const base = getBaseName(point.name);
      const groupBase = base.includes(" + ")
        ? resolveGroupForCombinedName(base, groupMap)
        : groupMap[base];

      if (groupBase) {
        const groupName = suffix
          ? `${groupBase} ${suffix}`
          : groupBase;

        if (groupEntries.has(groupName)) {
          /* Merge y values into existing group entry. */
          groupEntries.get(groupName)!.y = [
            ...groupEntries.get(groupName)!.y,
            ...point.y,
          ];
        } else {
          groupEntries.set(groupName, {
            ...point,
            name: groupName,
            y: [...point.y],
          });
        }
      } else {
        ungroupedEntries.push(point);
      }
    });

    return {
      ...item,
      data: [
        ...Array.from(groupEntries.values()),
        ...ungroupedEntries,
      ],
    };
  });
};

/**
 * Remove boxplots with empty y values.
 *
 * @param {TreatmentPatternsProps} data
 * @returns {SankeyBoxplotProps[]}
 */
const removeEmptyBoxplots = (
  data: TreatmentPatternsProps
): SankeyBoxplotProps[] => {
  const { boxplot } = data;

  return boxplot
    .map((item) => {
      const filteredData = item.data.filter((point) => {
        const { y } = point;
        return y.length > 1;
      });

      return {
        ...item,
        data: filteredData.map((point) => ({
          ...point,
          name: transform(point.name)
            .stripUnitSuffix()
            .toString(),
        })),
      };
    })
    .filter((item) => item.data.length > 0);
};

/**
 *
 * @param {TreatmentPatternsProps[]} data
 * @returns {void}
 */
export const assertValidSankeyEntries = (
  data: TreatmentPatternsProps[]
): void => {
  const invalid = data?.find((item) => {
    const { nodes, links } = item?.sankey ?? {};
    return (
      !Array.isArray(nodes) ||
      !nodes.length ||
      !Array.isArray(links) ||
      !links.length
    );
  });

  if (invalid) {
    throw new ClientError({
      type: GlobalMessageType.WARNING,
      message: ErrorMessage.NO_CONTENT,
      statusCode: HttpStatusCode.NoContent,
    });
  }
};

const isChartType = (t: any): t is ChartType =>
  Object.values(ChartType).includes(t);

/**
 * @param {TreatmentPatternsProps[]} data
 * @returns {ChartType[][]}
 */
const getChartTypes = (
  data: TreatmentPatternsProps[]
): ChartType[][] => {
  return (data ?? []).map((item) => {
    const types: ChartType[] = [];

    if (isChartType(item?.sankey?.type)) {
      types.push(item.sankey.type);
    }

    if (isChartType(item?.responseTable?.type)) {
      types.push(item.responseTable.type);
    }

    if (isChartType(item?.treatmentTable?.type)) {
      types.push(item.treatmentTable.type);
    }

    const boxplotTypes: ChartType[] = (item.boxplot ?? [])
      .map((box) => box.type)
      .filter(isChartType);

    return [...types, ...boxplotTypes];
  });
};

/**
 *
 */
export const extractUniqueTLs = (
  data: BoxplotProps[]
): string[] => {
  const tls = new Set<string>();

  data.forEach((item) => {
    const match = item.name.match(/\(TL\d+\)/);
    if (match) {
      tls.add(match[0].replace(/[()]/g, ""));
    }
  });

  return Array.from(tls).sort();
};

/**
 *
 */
export const filterBoxplotsByTL = (
  boxplots: SankeyBoxplotProps[],
  activeTLs: string[]
): SankeyBoxplotProps[] => {
  return boxplots
    .map((plot) => {
      const filteredData = plot.data.filter((entry) => {
        const match = entry.name.match(/\((TL\d+)\)/);
        const tl = match?.[1];
        return tl && activeTLs.includes(tl);
      });

      return {
        ...plot,
        data: filteredData,
      };
    })
    .filter((plot) => plot.data.length > 0);
};

/**
 * Group small nodes into an "Other" category in a Sankey diagram
 * to improve readability.
 *
 * @param {SankeyDiagramProps} data
 * @param {minAbsoluteWidth} minAbsoluteWidth
 * @param {string[]} expandedOthers
 * @param {Set<string>} protectedNodeNames  Nodes that must not be collapsed into "Other"
 * @returns {SankeyDiagramProps}
 */
export const groupSmallNodesToOther = (
  data: SankeyDiagramProps,
  minAbsoluteWidth: number,
  expandedOthers: string[] = [],
  protectedNodeNames: Set<string> = new Set()
): SankeyDiagramProps => {
  const { nodes, links } = data;

  const nodeTotals = computeNodeTotals(links);

  const markedNodes = markToGroupNodes(
    nodeTotals,
    minAbsoluteWidth,
    nodes,
    expandedOthers,
    protectedNodeNames
  );

  const { nodesToGroup, nodeTLMap } = markedNodes;

  const groupedNodes: NodeProps[] = [...nodes];
  const groupedLinks = createGroupedLinks(
    links,
    groupedNodes,
    nodesToGroup,
    nodeTLMap
  );

  const { finalLinks } =
    aggregateDuplicateLinks(groupedLinks);

  const reindexed = reindexSankeyDiagram(
    groupedNodes,
    finalLinks
  );

  return reindexed;
};

/**
 * Transform treatment data.
 *
 * Important steps:
 * - Filter invalid Sankey entries and remove empty boxplots.
 * - Group small nodes into "Other" based on a minimum absolute width.
 * - Support drilldown to show grouped nodes in detail.
 *
 * @param {BaseChartProps<TreatmentPatternsProps[]>} data
 * @param {MinAbsolutWidthProps} minAbsoluteWidth
 * @param {strings[]} expandedOthers
 * @returns {BaseChartProps<TreatmentPatternsProps[]>}
 */
export const transformTreatmentData = (
  data: BaseChartProps<
    TreatmentPatternsProps[],
    ChartType[][]
  >,
  width: number,
  expandedOthers: string[] = []
): BaseChartProps<
  TreatmentPatternsProps[],
  ChartType[][]
> => {
  assertValidSankeyEntries(data.data);

  // Order the per-cohort diagrams by information quality - real cohorts first
  // (backend order preserved), then "Looked for, no value", then "Not looked
  // for" - consistent with the descriptive/survival legends. Reorder the raw
  // entries so the parallel `types` array stays index-aligned with `data`.
  const orderedData = orderCohortGroups(data.data ?? []);

  const transformedData = orderedData.map(
    (item: TreatmentPatternsProps) => ({
      total: item.total,
      name:
        item.name === null
          ? Insights.FULL_SAMPLE
          : item.name,
      originalSankey: item.sankey,
      sankey: groupSmallNodesToOther(
        item.sankey,
        width,
        expandedOthers
      ),
      treatmentTable: item.treatmentTable,
      responseTable: item.responseTable,
      boxplot: removeEmptyBoxplots({
        ...item,
        boxplot: groupBoxplotsByTreatmentMap(
          item.boxplot,
          TREATMENT_GROUP_MAP
        ),
      }),
      totalCohortSize: item.totalCohortSize,
      notLookedForCount: item.notLookedForCount,
      lookedForNoValueCount: item.lookedForNoValueCount,
    })
  );

  return {
    ...data,
    types: getChartTypes(orderedData),
    data: transformedData,
  };
};
